import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconArrowsTransferDown,
  IconDotsVertical,
  IconGitBranch,
  IconPencil,
  IconPhone,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { E164_PATTERN, normalizeToE164 } from '@legacy-shared/phone';
import { ErrorAlert } from '@/components/error-alert';
import { useOrg } from '@/hooks/use-org';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { formatPhone } from './-components/format';
import {
  OrganizationFlowsSubscription,
  OrganizationPhoneNumbersSubscription,
  UpdatePhoneNumberFriendlyNameMutation,
} from './phone-numbers/-components/graphql';
import { SearchNumbersModal } from './phone-numbers/-components/search-modal';

const listedStatuses = ['active', 'pending'] as const;

type ListedStatus = (typeof listedStatuses)[number];

type ListedNumber = {
  id: string;
  phoneNumber: string;
  friendlyName: string | null;
  flowId: string | null;
  flowName: string | null;
  status: ListedStatus;
};

const statusStyles: Record<ListedStatus, { color: string; label: string; iconColor: string }> = {
  active: { color: 'green', label: 'Active', iconColor: 'var(--mantine-color-green-6)' },
  pending: { color: 'yellow', label: 'Pending', iconColor: 'var(--mantine-color-yellow-6)' },
};

function toListedStatus(status: string) {
  return listedStatuses.find((s) => s === status) ?? null;
}

function resolveDestructiveAction(
  status: ListedStatus,
  canManage: boolean,
  handlers: { release: () => void; remove: () => void },
) {
  if (!canManage) return null;
  if (status === 'active') return { label: 'Release Number', onClick: handlers.release };

  return { label: 'Remove', onClick: handlers.remove };
}

export const Route = createFileRoute('/_authed/phone-numbers')({
  component: PhoneNumbersPage,
});

function NumberRow({
  number,
  canManage,
  onRename,
  onAssignFlow,
  onRelease,
  onRemovePending,
}: {
  number: ListedNumber;
  canManage: boolean;
  onRename: (number: ListedNumber) => void;
  onAssignFlow: (number: ListedNumber) => void;
  onRelease: (number: ListedNumber) => void;
  onRemovePending: (number: ListedNumber) => void;
}) {
  const style = statusStyles[number.status];
  const destructiveAction = resolveDestructiveAction(number.status, canManage, {
    release: () => onRelease(number),
    remove: () => onRemovePending(number),
  });

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs">
          <IconPhone size={16} stroke={1.5} style={{ color: style.iconColor }} />
          <Text fw={500} c={number.status === 'pending' ? 'dimmed' : undefined}>
            {formatPhone(number.phoneNumber)}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text c={number.friendlyName ? undefined : 'dimmed'}>{number.friendlyName ?? 'No name'}</Text>
      </Table.Td>
      <Table.Td>
        {number.flowId ? (
          <Group gap="xs">
            <IconGitBranch size={14} />
            <Text size="sm">{number.flowName ?? 'Unknown'}</Text>
          </Group>
        ) : (
          <Text c="dimmed" size="sm">
            Not assigned
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Badge color={style.color} variant="light">
          {style.label}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray">
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => onRename(number)}>
              Rename
            </Menu.Item>
            <Menu.Item leftSection={<IconGitBranch size={14} />} onClick={() => onAssignFlow(number)}>
              Assign Flow
            </Menu.Item>
            {destructiveAction && (
              <>
                <Menu.Divider />
                <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={destructiveAction.onClick}>
                  {destructiveAction.label}
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  );
}

function EmptyState({ canManage, onSearch }: { canManage: boolean; onSearch: () => void }) {
  return (
    <Paper p="xl" radius="lg" withBorder>
      <Center>
        <Stack align="center" gap="md">
          <IconPhone size={48} stroke={1.5} style={{ color: 'var(--mantine-color-dimmed)' }} />
          <Text c="dimmed">No phone numbers yet</Text>
          {canManage && (
            <Button variant="light" onClick={onSearch}>
              Get Your First Number
            </Button>
          )}
        </Stack>
      </Center>
    </Paper>
  );
}

function PortedNumberModal({
  opened,
  organizationId,
  onClose,
}: {
  opened: boolean;
  organizationId: string;
  onClose: () => void;
}) {
  const [input, setInput] = useState('');
  const [friendlyName, setFriendlyName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalized = normalizeToE164(input);
  const isValid = E164_PATTERN.test(normalized);

  const close = () => {
    setInput('');
    setFriendlyName('');
    setError(null);
    onClose();
  };

  const submit = async () => {
    setIsSubmitting(true);
    setError(null);

    const result = await api['phone-numbers'].pending.post({
      organizationId,
      phoneNumber: normalized,
      friendlyName: friendlyName.trim() || undefined,
    });

    setIsSubmitting(false);

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to add number'));

    close();
  };

  return (
    <Modal opened={opened} onClose={close} title="Add Ported Number">
      <Stack>
        <Text size="sm" c="dimmed">
          Add a number that is being ported in. It stays pending until it shows up in the connected Telnyx account, then
          it is linked and activated automatically.
        </Text>
        <TextInput
          label="Phone Number"
          placeholder="(555) 123-4567"
          value={input}
          onChange={(event) => setInput(event.currentTarget.value)}
          error={input && !isValid ? 'Enter a valid phone number' : null}
        />
        {isValid && (
          <Text size="sm" c="dimmed">
            Will be saved as {formatPhone(normalized)} ({normalized})
          </Text>
        )}
        <TextInput
          label="Friendly Name"
          placeholder="e.g. Support Line"
          value={friendlyName}
          onChange={(event) => setFriendlyName(event.currentTarget.value)}
        />
        <ErrorAlert message={error} />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={isSubmitting} disabled={!isValid}>
            Add Number
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function PhoneNumbersPage() {
  const { organizationId, currentOrg, isPlatformOperatorView, isLoading } = useOrg();
  const canManage = isPlatformOperatorView || currentOrg?.role === 'owner' || currentOrg?.role === 'admin';

  const [{ data: numbersData }] = useSubscription({
    query: OrganizationPhoneNumbersSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  const [{ data: flowsData }] = useSubscription({
    query: OrganizationFlowsSubscription,
    variables: { organizationId: organizationId ?? '' },
    pause: !organizationId,
  });

  const [renameState, renameNumber] = useMutation(UpdatePhoneNumberFriendlyNameMutation);

  const [searchOpen, setSearchOpen] = useState(false);
  const [portedOpen, setPortedOpen] = useState(false);
  const [renaming, setRenaming] = useState<ListedNumber | null>(null);
  const [draftName, setDraftName] = useState('');
  const [assigning, setAssigning] = useState<ListedNumber | null>(null);
  const [draftFlowId, setDraftFlowId] = useState<string | null>(null);
  const [releasing, setReleasing] = useState<ListedNumber | null>(null);
  const [removingPending, setRemovingPending] = useState<ListedNumber | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isLoading || (organizationId && numbersData === undefined)) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId) {
    return (
      <Center h="50vh">
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  const listedNumbers = (numbersData?.phoneNumbers ?? []).flatMap<ListedNumber>((n) => {
    const status = toListedStatus(n.status);

    if (!status) return [];

    return [
      {
        id: n.id,
        phoneNumber: n.phoneNumber,
        friendlyName: n.friendlyName,
        flowId: n.flowId,
        flowName: n.flow?.name ?? null,
        status,
      },
    ];
  });

  const flowOptions = (flowsData?.flows ?? []).map((f) => ({ value: f.id, label: f.name }));

  const openRename = (number: ListedNumber) => {
    setError(null);
    setRenaming(number);
    setDraftName(number.friendlyName ?? '');
  };

  const openAssign = (number: ListedNumber) => {
    setError(null);
    setAssigning(number);
    setDraftFlowId(number.flowId);
  };

  const openRelease = (number: ListedNumber) => {
    setError(null);
    setReleasing(number);
  };

  const openRemovePending = (number: ListedNumber) => {
    setError(null);
    setRemovingPending(number);
  };

  const submitRename = async () => {
    if (!renaming) return;

    setError(null);

    const result = await renameNumber({ id: renaming.id, friendlyName: draftName.trim() || null });

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to rename number'));

    setRenaming(null);
  };

  const submitAssign = async () => {
    if (!assigning) return;

    setIsSubmitting(true);
    setError(null);

    const result = await api['phone-numbers']({ id: assigning.id })['assign-flow'].post({ flowId: draftFlowId });

    setIsSubmitting(false);

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to assign flow'));

    setAssigning(null);
  };

  const submitRelease = async () => {
    if (!releasing) return;

    setIsSubmitting(true);
    setError(null);

    const result = await api['phone-numbers']({ id: releasing.id }).release.post();

    setIsSubmitting(false);

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to release number'));

    setReleasing(null);
  };

  const submitRemovePending = async () => {
    if (!removingPending) return;

    setIsSubmitting(true);
    setError(null);

    const result = await api['phone-numbers'].pending({ id: removingPending.id }).delete();

    setIsSubmitting(false);

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to remove number'));

    setRemovingPending(null);
  };

  return (
    <>
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Group justify="space-between">
            <div>
              <Title order={1}>Phone Numbers</Title>
              <Text c="dimmed" mt="xs">
                Manage your Telnyx phone numbers
              </Text>
            </div>
            {canManage && (
              <Group gap="sm">
                <Button
                  variant="light"
                  leftSection={<IconArrowsTransferDown size={16} />}
                  onClick={() => setPortedOpen(true)}
                >
                  Add Ported Number
                </Button>
                <Button leftSection={<IconPlus size={16} />} onClick={() => setSearchOpen(true)}>
                  Get New Number
                </Button>
              </Group>
            )}
          </Group>

          {listedNumbers.length === 0 ? (
            <EmptyState canManage={canManage} onSearch={() => setSearchOpen(true)} />
          ) : (
            <Paper radius="lg" withBorder>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Phone Number</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Assigned Flow</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th w={60} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {listedNumbers.map((number) => (
                    <NumberRow
                      key={number.id}
                      number={number}
                      canManage={canManage}
                      onRename={openRename}
                      onAssignFlow={openAssign}
                      onRelease={openRelease}
                      onRemovePending={openRemovePending}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </Stack>
      </Container>

      <SearchNumbersModal opened={searchOpen} organizationId={organizationId} onClose={() => setSearchOpen(false)} />

      <PortedNumberModal opened={portedOpen} organizationId={organizationId} onClose={() => setPortedOpen(false)} />

      <Modal opened={assigning !== null} onClose={() => setAssigning(null)} title="Assign Flow">
        <Stack>
          <ErrorAlert message={error} />
          <Select
            label="Flow"
            placeholder="Select a flow"
            value={draftFlowId}
            onChange={setDraftFlowId}
            data={flowOptions}
            clearable
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAssigning(null)}>
              Cancel
            </Button>
            <Button onClick={() => void submitAssign()} loading={isSubmitting}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={renaming !== null} onClose={() => setRenaming(null)} title="Rename Number">
        <Stack>
          <ErrorAlert message={error} />
          <TextInput
            label="Friendly Name"
            placeholder="e.g. Support Line"
            value={draftName}
            onChange={(event) => setDraftName(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
            <Button onClick={() => void submitRename()} loading={renameState.fetching}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={releasing !== null} onClose={() => setReleasing(null)} title="Release Number">
        <Stack>
          <ErrorAlert message={error} />
          <Text>Are you sure you want to release this number? This action cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setReleasing(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void submitRelease()} loading={isSubmitting}>
              Release
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={removingPending !== null} onClose={() => setRemovingPending(null)} title="Remove Pending Number">
        <Stack>
          <ErrorAlert message={error} />
          <Text>
            This removes the local record only. The number was never linked to Telnyx, so nothing is released or
            cancelled on the carrier side.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRemovingPending(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void submitRemovePending()} loading={isSubmitting}>
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
